import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ACTIVATION_TYPE, ROUTE } from '@lib/core/const';
import { PERMISSION } from '@lib/core/enum';
import { Uuid } from '@lib/core/type';
import { BlockedElementComponent } from '@lib/ui';
import { TranslatedDialogService } from '@proget-shared/dialog/translated-dialog';
import { GridAction, GridActionPrompt, GridActionType, GridActionsModule, SelectionStrategy } from '@proget-shared/grid/grid-actions';
import { GridControl, GridControlService, PagingList } from '@proget-shared/grid/grid-control';
import { SelectionService } from '@proget-shared/grid/selection';
import { TableComponent, TableModule } from '@proget-shared/grid/table';
import { TableActionsModule } from '@proget-shared/grid/table-actions';
import { TableExportConfigurationDialogComponent } from '@proget-shared/grid/table-export-configuration-dialog';
import { PipeModule } from '@proget-shared/pipe';
import { QueryParamsService } from '@proget-shared/query-params';
import { TranslateModule } from '@proget-shared/translate';
import { EllipsisModule } from '@proget-shared/ui/ellipsis';
import { LinkModule } from '@proget-shared/ui/link';
import { TableCellComponent } from '@proget-shared/ui/table-cell';
import { ToastService } from '@proget-shared/ui/toast';
import { BehaviorSubject, combineLatest } from 'rxjs';

import { GenerateReportComponent } from '../../../component/generate-report/generate-report.component';
import { SendAlertComponent } from '../../../component/send-alert/send-alert.component';
import { SendMessageComponent } from '../../../component/send-message/send-message.component';
import { DeviceListItem } from '../../../model';
import { SignalStrengthPipe } from '../../../pipe/signal-strength.pipe';
import { UserCardService } from '../../../service/user-card.service';
import { UserCardRepository } from '../../../user-card.repository';

@Component({
  selector: 'lib-user-card-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
  imports: [
    CommonModule,
    RouterLink,
    BlockedElementComponent,
    GridActionsModule,
    TableModule,
    TableActionsModule,
    TableCellComponent,
    EllipsisModule,
    LinkModule,
    PipeModule,
    TranslateModule,
    SignalStrengthPipe,
  ],
  providers: [GridControlService, QueryParamsService, SelectionService],
})
export class DevicesComponent {
  protected readonly devicesGridControl: GridControl;
  protected readonly PERMISSION = PERMISSION;
  protected readonly selectionService = inject(SelectionService);

  protected devicesActions: GridAction<DeviceListItem>[] = [];
  protected items: PagingList<DeviceListItem> | undefined;

  private readonly devicesTable = viewChild<TableComponent>('devicesTable');
  private readonly destroyRef = inject(DestroyRef);
  private readonly userCardRepository = inject(UserCardRepository);
  private readonly translatedDialogService = inject(TranslatedDialogService);
  private readonly toastService = inject(ToastService);
  private readonly reloadSubject = new BehaviorSubject<boolean>(true);

  constructor() {
    const userCardService = inject(UserCardService);
    const gridControlService = inject(GridControlService);

    this.devicesGridControl = gridControlService.registerGridControl('userCardDevices');

    combineLatest([
      this.devicesGridControl.requestParams$,
      userCardService.user$,
      this.reloadSubject,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([params, user]) => {
          this.loadDevices(params, user.uuid);
        },
      });

    this.prepareActions();
  }

  private loadDevices(params: any, userId: Uuid): void {
    this.userCardRepository.getAllUserDevices(userId, params).subscribe({
      next: (items) => {
        this.items = items;
        this.selectionService.setVisibleItems(this.items.items);
        this.devicesGridControl.setItemsCount(this.items.total);
      },
      error: () => {
        this.items = new PagingList();
        this.devicesGridControl.setItemsCount(0);
        this.selectionService.setVisibleItems([]);
      },
    });
  }

  private reloadDevices(): void {
    this.reloadSubject.next(true);
  }

  private prepareActions(): void {
    this.devicesActions = [
      {
        label: 'library.user_card.devices.list.action.reset_password.header',
        icon: 'icon-refresh-pass-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: (item): boolean => !item.locked && item.active &&
          item.activation !== ACTIVATION_TYPE.WINDOWS_MDM.KEY && item.activation !== ACTIVATION_TYPE.MACOS_MDM.KEY,
        prompt: {
          type: GridActionPrompt.CONFIRM,
          header: 'library.user_card.devices.list.action.reset_password.header',
          message: 'confirm_action.message',
        },
        type: GridActionType.BATCH_WORK,
        batchWorkConfiguration: {
          actionKey: 'library.user_card.devices.list.action.reset_password.header',
        },
        action: (itemId: Uuid) => this.userCardRepository.resetDevicePassword(itemId),
      },
      {
        label: 'library.user_card.devices.list.action.send_message.header',
        icon: 'icon-send-msg-device-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: (item): boolean => !item.locked &&
          item.active &&
          item.activation !== ACTIVATION_TYPE.IOS_MDM.KEY &&
          item.activation !== ACTIVATION_TYPE.MACOS_MDM.KEY &&
          item.activation !== ACTIVATION_TYPE.WINDOWS_MDM.KEY,
        prompt: {
          type: GridActionPrompt.CUSTOM,
          component: SendMessageComponent,
        },
        type: GridActionType.ACTION_MULTI,
        action: (items: DeviceListItem[], data: any) => this.userCardRepository.sendDeviceMessage(
          Object.assign({}, { id: items.map((device: DeviceListItem) => device.id) }, data)
        ),
        success: {
          toast: 'library.user_card.toast.send_message.success.title',
        },
        error: {
          toast: 'library.user_card.toast.send_message.error.title',
        },
      },
      {
        label: 'library.user_card.devices.list.action.send_alarm_message.header',
        icon: 'icon-send-alert-msg-device-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: (item): boolean => !item.locked &&
          item.active &&
          item.activation !== ACTIVATION_TYPE.IOS_MDM.KEY &&
          item.activation !== ACTIVATION_TYPE.MACOS_MDM.KEY &&
          item.activation !== ACTIVATION_TYPE.WINDOWS_MDM.KEY,
        prompt: {
          type: GridActionPrompt.CUSTOM,
          component: SendAlertComponent,
        },
        type: GridActionType.ACTION_MULTI,
        action: (items: DeviceListItem[], data: any) => this.userCardRepository.sendDeviceAlarmMessage(
          Object.assign({}, { id: items.map((device: DeviceListItem) => device.id) }, data)
        ),
        success: {
          toast: 'library.user_card.toast.send_alarm_message.success.title',
        },
        error: {
          toast: 'library.user_card.toast.send_alarm_message.error.title',
        },
      },
      {
        label: 'library.user_card.devices.list.action.generate_report.header',
        icon: 'icon-reports-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: (item): boolean => item.active &&
          item.activation !== ACTIVATION_TYPE.IOS_MDM.KEY &&
          item.activation !== ACTIVATION_TYPE.MACOS_MDM.KEY &&
          item.activation !== ACTIVATION_TYPE.WINDOWS_MDM.KEY,
        prompt: {
          type: GridActionPrompt.CUSTOM,
          component: GenerateReportComponent,
        },
        type: GridActionType.ACTION_MULTI,
        action: (items: DeviceListItem[], data: any) => this.userCardRepository.generateDeviceReport(
          items.map((device: DeviceListItem) => device.id),
          data.foreground,
          data.foregroundFrom,
          data.foregroundTo,
          data.mobile,
          data.mobileFrom,
          data.mobileTo,
          data.network,
          data.networkFrom,
          data.networkTo,
          data.storage
        ),
        success: {
          toast: 'library.user_card.toast.generate_report.success.title',
        },
        error: {
          toast: 'library.user_card.toast.generate_report.error.title',
        },
      },
      {
        label: 'library.user_card.devices.list.action.export_data.header',
        icon: 'icon-export-info-icon',
        permission: [PERMISSION.USER_CARD_ALLOW_MANAGE],
        prompt: {
          type: GridActionPrompt.CUSTOM,
          component: TableExportConfigurationDialogComponent,
          options: {
            configuration: {
              header: 'library.user_card.devices.list.action.export_data.header',
              icon: 'icon-export-info-icon',
              getTable: () => this.devicesTable(),
            },
          },
        },
        type: GridActionType.ACTION_MULTI,
        action: (items: DeviceListItem[], configuration: any) => this.userCardRepository.exportDeviceData({
          name: configuration.name,
          ids: items.map((item) => item.id),
          headers: configuration.columns,
          order: configuration.order.column ? configuration.order : void 0,
        }),
        success: {
          callback: (): void => {
            this.exportSuccessCallback();
          },
        },
        error: {
          toast: 'library.user_card.toast.export_data.error.title',
        },
      },
      {
        label: 'library.user_card.devices.list.action.refresh_device.header',
        icon: 'icon-refresh-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: (item): boolean => item.active,
        prompt: {
          type: GridActionPrompt.CONFIRM,
          header: 'library.user_card.devices.list.action.refresh_device.header',
          message: 'confirm_action.message',
        },
        type: GridActionType.BATCH_WORK,
        batchWorkConfiguration: {
          actionKey: 'library.user_card.devices.list.action.refresh_device.header',
        },
        action: (itemId: Uuid) => this.userCardRepository.refreshDevice(itemId),
      },
      {
        label: 'library.user_card.devices.list.action.delete.header',
        icon: 'icon-delete-icon',
        permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
        enabled: (item): boolean => !item.dep && !item.active,
        prompt: {
          type: GridActionPrompt.PHRASE,
          header: 'library.user_card.devices.list.action.delete.header',
          phrase: 'Delete',
        },
        type: GridActionType.BATCH_WORK,
        batchWorkConfiguration: {
          actionKey: 'library.user_card.devices.list.action.delete.header',
        },
        action: (itemId: Uuid) => this.userCardRepository.deleteDevice(itemId),
        error: {
          callback: (error: string): void => {
            this.translatedDialogService.alert(
              'library.user_card.dialog.delete.header',
              error,
              { configuration: { iconClass: 'icon-dangerous-icon' } }
            );
          },
        },
        finalize: {
          selectionStrategy: SelectionStrategy.ALWAYS_DESELECT,
          callback: (): void => {
            this.reloadDevices();
          },
        },
      },
    ];
  }

  private exportSuccessCallback(): void {
    this.toastService.success(
      'library.user_card.toast.export_data.success.title',
      'library.user_card.toast.export_data.success.message',
      {
        duration: 10000,
        link: {
          route: [ROUTE.REPORT.BASE],
          label: 'library.user_card.toast.export_data.success.link',
        },
      }
    );
  }
}
